import { useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';

/**
 * A select over a small fixed vocabulary, drawn by the page rather than by the platform.
 *
 * It exists because a native `<option>` holds text and nothing else: no status square, no colour.
 * The list, the board column heads and the row meta all say a status as its mark and a priority
 * in its colour, so a native picker made the detail pane and the create form the two places on
 * the page where those are only words — and the mark is the vocabulary the whole page is read in.
 *
 * Everything else here is what `<select>` gives for free and has to be paid for once the control
 * is a button and a list: the combobox roles, the arrow keys, Escape, and closing when focus
 * leaves. The option rows are chosen on `click` with `mousedown` defaulted away, so the button
 * still holds focus when the click lands — the same trick, and for the same reason, as the
 * search box's suggestion list.
 */
export function Picker<T extends string>({ value, options, label, render, onChange }: {
  value: T;
  options: readonly T[];
  /** What the control is — "Status", "Priority". Its accessible name is this and the value. */
  label: string;
  render: (option: T) => ReactNode;
  onChange: (option: T) => void;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);
  const id = useId();
  const current = Math.max(0, options.indexOf(value));

  // Opening starts the cursor on what is selected rather than on wherever the last pass left it,
  // which would otherwise highlight a row this opening did not choose.
  useEffect(() => { if (open) setActive(current); }, [open, current]);
  useEffect(() => { if (open) listRef.current?.querySelector('.active')?.scrollIntoView({ block: 'nearest' }); }, [active, open]);

  const choose = (option: T) => {
    setOpen(false);
    if (option !== value) onChange(option);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      setActive((active + (e.key === 'ArrowDown' ? 1 : options.length - 1)) % options.length);
      return;
    }
    if (e.key === 'Home' || e.key === 'End') { if (open) { e.preventDefault(); setActive(e.key === 'Home' ? 0 : options.length - 1); } return; }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      const option = options[active];
      if (option !== undefined) choose(option);
      return;
    }
    if (e.key === 'Escape' && open) { e.preventDefault(); setOpen(false); }
  };

  return (
    <div className="picker">
      {/* The name is the label AND the value, which is why the label is a node of its own: an
          `aria-label` on the button would replace the value it is there to qualify. */}
      <span id={`${id}-label`} className="sr-only">{label}</span>
      <button
        type="button" className="picker-value"
        role="combobox" aria-haspopup="listbox" aria-expanded={open}
        aria-controls={open ? `${id}-list` : undefined}
        aria-labelledby={`${id}-label ${id}-value`}
        aria-activedescendant={open ? `${id}-${active}` : undefined}
        onClick={() => setOpen(!open)}
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
      >
        <span id={`${id}-value`} className="picker-shown">{render(value)}</span>
        <span className="picker-caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <ul className="picker-menu" role="listbox" id={`${id}-list`} ref={listRef} aria-labelledby={`${id}-label`}>
          {options.map((option, i) => (
            <li
              key={option} id={`${id}-${i}`} role="option" aria-selected={option === value}
              className={i === active ? 'active' : undefined}
              // `mousemove`, not `mouseenter`: the list opens under a resting pointer, and
              // `mouseenter` would move the cursor off a row the keyboard just chose.
              onMouseMove={() => setActive(i)}
              onMouseDown={e => e.preventDefault()}
              onClick={() => choose(option)}
            >
              <span className="picker-check" aria-hidden="true">{option === value ? '✓' : ''}</span>
              {render(option)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
